import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Perk } from 'components/loyalty/models/PerkModel';
import { perkApi } from 'components/loyalty/PerkApi';
import { removeArrayItem, updateArrayItem } from 'lib/helpers';
import logger from 'lib/logger';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { AppDispatch } from '../store';

export interface PerksState {
    perks: Perk[];
    loading: boolean;
    createModalOpen: boolean;
    closeCreateModalOpen: boolean;
    closeUpdateModalOpen: boolean;
    stepClub: boolean;
    indexToReset: number;
    indexToDelete: number;
    indexOpen: number;
    indexToDuplicate: number;
    resetModalLoading: boolean;
    deleteModalLoading: boolean;
    perkIdToTitle: Map<string, string>;
    perkAwardCount: number;
    selectedRow: GridRowId[];
}

export const initialState: PerksState = {
    perks: [],
    loading: false,
    createModalOpen: false,
    closeUpdateModalOpen: false,
    closeCreateModalOpen: false,
    stepClub: false,
    indexToReset: -1,
    indexToDelete: -1,
    indexOpen: -1,
    indexToDuplicate: -1,
    resetModalLoading: false,
    deleteModalLoading: false,
    perkIdToTitle: new Map<string, string>(),
    perkAwardCount: -1,
    selectedRow: []
};

const perksSlice = createSlice({
    name: 'perks',
    initialState,
    reducers: {
        startRequest(state) {
            state.loading = true;
        },
        loadSuccess(state, action: PayloadAction<{ perks?: Perk[] }>) {
            state.loading = false;
            state.perks = action.payload.perks ?? [];
        },
        loadAllSuccess(state, action: PayloadAction<{ perks?: Perk[]; perkIdToTitle: Map<string, string> }>) {
            state.loading = false;
            state.perkIdToTitle = action.payload.perkIdToTitle;
            state.perks = action.payload.perks ?? [];
        },
        loadError(state) {
            state.loading = false;
            state.perks = [];
        },
        createSuccess(state, action: PayloadAction<{ perk: Perk }>) {
            state.closeCreateModalOpen = false;
            state.createModalOpen = false;
            state.loading = false;
            state.indexToDuplicate = -1;
            state.perks = [...state.perks, action.payload.perk];
        },
        createSuccessClub(state, action: PayloadAction<{ perk: Perk }>) {
            state.closeCreateModalOpen = false;
            state.createModalOpen = true;
            state.stepClub = true;
            state.indexToDuplicate = -1;
            state.perks = [...state.perks, action.payload.perk];
        },
        toggleCreateModal(state) {
            state.createModalOpen = !state.createModalOpen;
        },
        toggleCloseCreateModal(state) {
            state.closeCreateModalOpen = !state.closeCreateModalOpen;
        },
        toggleCloseUpdateModal(state) {
            state.closeUpdateModalOpen = !state.closeUpdateModalOpen;
        },
        closeModals(state) {
            state.closeCreateModalOpen = false;
            state.createModalOpen = false;
            state.closeUpdateModalOpen = false;
            state.indexOpen = -1;
            state.indexToDuplicate = -1;
            state.stepClub = false;
        },
        startDeleteRequest(state) {
            state.deleteModalLoading = true;
        },
        setItemToReset(state, action: PayloadAction<number>) {
            state.indexToReset = action.payload;
        },
        setPerkAwardCount(state, action: PayloadAction<number>) {
            state.perkAwardCount = action.payload;
        },
        setItemToDelete(state, action: PayloadAction<number>) {
            state.indexToDelete = action.payload;
        },
        resetSuccess(state) {
            state.indexToReset = -1;
            state.resetModalLoading = false;
            state.selectedRow = [];
        },
        resetError(state) {
            state.indexToReset = -1;
            state.resetModalLoading = false;
        },
        deleteError(state) {
            state.indexToDelete = -1;
            state.deleteModalLoading = false;
        },
        deleteSuccess(state) {
            state.perks = removeArrayItem(state.perks, state.indexToDelete);
            state.indexToDelete = -1;
            state.deleteModalLoading = false;
        },
        setOpenItem(state, action: PayloadAction<number>) {
            state.indexOpen = action.payload;
        },
        setItemToDuplicate(state, action: PayloadAction<number>) {
            state.indexToDuplicate = action.payload;
        },
        updateSuccess(state, action: PayloadAction<{ perk: Perk }>) {
            state.perks = updateArrayItem(state.perks, state.indexOpen, action.payload.perk);
            state.indexOpen = -1;
            state.selectedRow = [];
        },
        setSelectedRow(state, action: PayloadAction<GridRowSelectionModel>) {
            state.selectedRow = action.payload.at(-1) === state.selectedRow.at(-1) ? [] : [...action.payload];
        },
        startResetRequest(state) {
            state.resetModalLoading = true;
        }
    }
});

export const {
    startRequest,
    loadSuccess,
    loadAllSuccess,
    loadError,
    createSuccess,
    createSuccessClub,
    toggleCreateModal,
    toggleCloseCreateModal,
    toggleCloseUpdateModal,
    closeModals,
    startDeleteRequest,
    setItemToReset,
    setPerkAwardCount,
    setItemToDelete,
    resetSuccess,
    resetError,
    deleteError,
    deleteSuccess,
    setOpenItem,
    setItemToDuplicate,
    updateSuccess,
    setSelectedRow,
    startResetRequest
} = perksSlice.actions;

// Async thunks
export function getAllPerks() {
    return async function (dispatch: AppDispatch) {
        dispatch(startRequest());
        try {
            const pepperPerksResponse = await perkApi.getList();

            if (!pepperPerksResponse.ok) {
                throw new Error(`Failed to load perks with code: ${pepperPerksResponse.statusCode}`);
            }
            // eslint-disable-next-line prefer-destructuring
            const perks: Perk[] = pepperPerksResponse.body.perks;
            const perksMap = new Map<string, string>();
            if (Array.isArray(perks)) {
                perks.forEach(perk => {
                    const { _id, title, internalTitle } = perk ?? {};
                    let finalTitle = '';
                    if ((title ?? '').trim()) {
                        if (internalTitle) {
                            finalTitle = `${internalTitle} - ${title}`;
                        } else {
                            finalTitle = title;
                        }
                    } else if (internalTitle) {
                        finalTitle = internalTitle;
                    }
                    perksMap.set(_id, finalTitle);
                });
            }

            dispatch(loadAllSuccess({ perkIdToTitle: perksMap, perks }));
        } catch (e) {
            logger.error('Failed to load all perks', e);
            dispatch(loadError());
        }
    };
}

export default perksSlice.reducer;
